import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import SimpleModal from "components/SimpleModal";
import api from "services/api";

import { useTranslation } from "react-i18next";

function SubscribeModal({ open, onClose }) {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("subscription");
  const [linkGroups, setLinkGroups] = useState({
    subscription: { main: "", backup: "" },
    clash: { main: "", backup: "" },
  });
  const [subError, setSubError] = useState("");
  const [revealState, setRevealState] = useState({
    subscriptionMain: false,
    subscriptionBackup: false,
    clashMain: false,
    clashBackup: false,
  });
  const [copiedKey, setCopiedKey] = useState("");
  const hideTimersRef = useRef({
    subscriptionMain: null,
    subscriptionBackup: null,
    clashMain: null,
    clashBackup: null,
  });

  const SUBSCRIPTION_LINK_ENDPOINT = "/auth/sublinks/";
  const HIDE_DELAY_MS = 2000;

  const clearHideTimer = (key) => {
    const timerId = hideTimersRef.current[key];
    if (timerId) {
      clearTimeout(timerId);
      hideTimersRef.current[key] = null;
    }
  };

  const clearAllHideTimers = () => {
    clearHideTimer("subscriptionMain");
    clearHideTimer("subscriptionBackup");
    clearHideTimer("clashMain");
    clearHideTimer("clashBackup");
  };

  const resetSensitiveState = () => {
    clearAllHideTimers();
    setActiveTab("subscription");
    setLinkGroups({
      subscription: { main: "", backup: "" },
      clash: { main: "", backup: "" },
    });
    setSubError("");
    setRevealState({
      subscriptionMain: false,
      subscriptionBackup: false,
      clashMain: false,
      clashBackup: false,
    });
    setCopiedKey("");
    setIsLoading(false);
  };

  const findFirstValid = (values) =>
    (values.find((item) => typeof item === "string" && item.trim()) || "").trim();

  const buildSubscriptionUrlFromHostToken = (host, token, format) => {
    const baseHost = typeof host === "string" ? host.trim() : "";
    const safeToken = typeof token === "string" ? token.trim() : "";
    const mode = format === "clash" ? "c" : "v";
    if (!baseHost || !safeToken) return "";

    return `${baseHost.replace(/\/+$/, "")}/apiv2/sub/${mode}/${encodeURIComponent(safeToken)}/`;
  };

  const extractLinkGroups = (payload) => {
    const source = payload?.data && typeof payload.data === "object" ? payload.data : payload || {};

    const token = findFirstValid([source.token, source.sub_token, source.subscribe_token]);
    const subHost = findFirstValid([source.sub_host, source.host]);
    const altHost = findFirstValid([source.alt_host, source.backup_host]);

    const constructedSubscriptionMain = buildSubscriptionUrlFromHostToken(subHost, token, "v2ray");
    const constructedSubscriptionBackup = buildSubscriptionUrlFromHostToken(
      altHost,
      token,
      "v2ray"
    );
    const constructedClashMain = buildSubscriptionUrlFromHostToken(subHost, token, "clash");
    const constructedClashBackup = buildSubscriptionUrlFromHostToken(altHost, token, "clash");

    const subscriptionMainCandidates = [
      source.sub_link,
      source.subscription_url,
      source.subscription_link,
      source.subscribe_url,
      source.subscribe_link,
      source.url,
      source.link,
      constructedSubscriptionMain,
    ];
    const subscriptionBackupCandidates = [
      source.sub_link_alt,
      source.subscription_url_alt,
      source.backup_link,
      constructedSubscriptionBackup,
    ];
    const clashMainCandidates = [
      source.clash_link,
      source.clash_url,
      source.clash_subscription_url,
      constructedClashMain,
    ];
    const clashBackupCandidates = [
      source.clash_link_alt,
      source.clash_url_alt,
      source.clash_backup_link,
      constructedClashBackup,
    ];

    const subscriptionMain = findFirstValid(subscriptionMainCandidates);
    const subscriptionBackup = findFirstValid(subscriptionBackupCandidates);
    const clashMain = findFirstValid(clashMainCandidates);
    const clashBackup = findFirstValid(clashBackupCandidates);

    return {
      subscription: { main: subscriptionMain, backup: subscriptionBackup },
      clash: { main: clashMain, backup: clashBackup },
    };
  };

  const loadSubscriptionLink = async () => {
    setIsLoading(true);
    setSubError("");
    setLinkGroups({
      subscription: { main: "", backup: "" },
      clash: { main: "", backup: "" },
    });
    setRevealState({
      subscriptionMain: false,
      subscriptionBackup: false,
      clashMain: false,
      clashBackup: false,
    });
    setCopiedKey("");
    try {
      const res = await api.get(SUBSCRIPTION_LINK_ENDPOINT);
      const resolvedGroups = extractLinkGroups(res?.data);
      const hasAnyLink =
        resolvedGroups.subscription.main ||
        resolvedGroups.subscription.backup ||
        resolvedGroups.clash.main ||
        resolvedGroups.clash.backup;
      if (!hasAnyLink) {
        throw new Error("subscription_link_missing");
      }
      setLinkGroups(resolvedGroups);
    } catch (err) {
      setSubError(t("userInfo.subscriptionModal.fetchError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopySubLink = async (value, key) => {
    if (!value || isLoading) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const input = document.createElement("textarea");
        input.value = value;
        input.setAttribute("readonly", "");
        input.style.position = "absolute";
        input.style.left = "-9999px";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1200);
    } catch (err) {
      setSubError(t("userInfo.subscriptionModal.copyError"));
    }
  };

  useEffect(() => {
    if (open) {
      loadSubscriptionLink();
    } else {
      resetSensitiveState();
    }
  }, [open]);

  useEffect(() => () => clearAllHideTimers(), []);

  const handleClose = () => {
    resetSensitiveState();
    onClose();
  };

  const currentTabLinks = linkGroups[activeTab] || { main: "", backup: "" };
  const warningDescription = t("userInfo.subscriptionModal.description");
  const compatibleAppsText =
    activeTab === "subscription"
      ? t("userInfo.subscriptionModal.compatibleApps.subscription")
      : t("userInfo.subscriptionModal.compatibleApps.clash");

  const linkItems = [
    {
      key: "main",
      revealKey: activeTab === "subscription" ? "subscriptionMain" : "clashMain",
      label: t("userInfo.subscriptionModal.primaryLabel"),
      value: currentTabLinks.main,
    },
    {
      key: "backup",
      revealKey: activeTab === "subscription" ? "subscriptionBackup" : "clashBackup",
      label: t("userInfo.subscriptionModal.backupLabel"),
      value: currentTabLinks.backup,
    },
  ].filter((item) => item.value);

  return (
    <SimpleModal
      open={open}
      title={t("userInfo.subscriptionModal.title")}
      showCancel={false}
      showConfirm={false}
      onClose={handleClose}
    >
      <MKTypography variant="body2" color="error" fontWeight="medium" mb={1}>
        {warningDescription}
      </MKTypography>
      <Tabs
        value={activeTab}
        onChange={(_, value) => {
          setActiveTab(value);
          setCopiedKey("");
        }}
        sx={{ minHeight: 36, mb: 1 }}
      >
        <Tab
          value="subscription"
          label={t("userInfo.subscriptionModal.tabs.subscription")}
          sx={{ minHeight: 36, py: 0.5 }}
        />
        <Tab
          value="clash"
          label={t("userInfo.subscriptionModal.tabs.clash")}
          sx={{ minHeight: 36, py: 0.5 }}
        />
      </Tabs>
      <MKTypography variant="caption" color="text" display="block" mb={1}>
        {compatibleAppsText}
      </MKTypography>

      {isLoading ? (
        <MKBox display="flex" alignItems="center" gap={1.2} py={1}>
          <CircularProgress size={18} />
          <MKTypography variant="body2" color="text">
            {t("userInfo.subscriptionModal.loading")}
          </MKTypography>
        </MKBox>
      ) : null}

      {!isLoading && subError ? (
        <MKBox>
          <MKTypography variant="body2" color="error" mb={1}>
            {subError}
          </MKTypography>
          <MKButton variant="outlined" color="info" size="small" onClick={loadSubscriptionLink}>
            {t("userInfo.subscriptionModal.retry")}
          </MKButton>
        </MKBox>
      ) : null}

      {!isLoading && !subError && linkItems.length > 0 ? (
        <>
          {linkItems.map((item) => (
            <MKBox
              key={item.revealKey}
              mb={item.key === "main" && currentTabLinks.backup ? 1.25 : 0}
            >
              <MKTypography variant="caption" color="text" display="block" mb={0.5}>
                {item.label}
              </MKTypography>
              <MKBox
                mt={0.2}
                px={1.5}
                py={1.2}
                borderRadius="md"
                bgColor="grey-100"
                border="1px solid"
                borderColor="grey-300"
                onMouseEnter={() => {
                  clearHideTimer(item.revealKey);
                  setRevealState((prev) => ({ ...prev, [item.revealKey]: true }));
                }}
                onMouseLeave={() => {
                  clearHideTimer(item.revealKey);
                  hideTimersRef.current[item.revealKey] = setTimeout(() => {
                    setRevealState((prev) => ({ ...prev, [item.revealKey]: false }));
                    hideTimersRef.current[item.revealKey] = null;
                  }, HIDE_DELAY_MS);
                }}
                sx={{ userSelect: "text", wordBreak: "break-all" }}
              >
                <MKTypography
                  variant="caption"
                  color="text"
                  sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                >
                  {revealState[item.revealKey]
                    ? item.value
                    : t("userInfo.subscriptionModal.hoverToReveal")}
                </MKTypography>
              </MKBox>
              <MKButton
                variant="gradient"
                color="info"
                size="small"
                sx={{ mt: 1 }}
                onClick={() => handleCopySubLink(item.value, item.revealKey)}
              >
                {copiedKey === item.revealKey
                  ? t("userInfo.subscriptionModal.copied")
                  : t("userInfo.subscriptionModal.copyButton")}
              </MKButton>
            </MKBox>
          ))}
        </>
      ) : null}
    </SimpleModal>
  );
}

SubscribeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SubscribeModal;
